import { useCallback, useRef, useState } from 'react'
import { useDebouncedCallback } from './use_debounced_callback'
import { useAppSelector } from './state'
import { getResourceAuthorization } from '../state/selectors/resource_authorization'
import { IIbanCheckResponse, ibanCheck } from '../api/mutations'
import { isValidDutchIban, normalizeDutchIban } from '../utils/iban'

type IIbanCheckError = {
  message?: string
  response?: {
    data?: IIbanCheckResponse
  }
}

const getSuggestion = (response?: IIbanCheckResponse) => {
  if (!response || Array.isArray(response.data)) {
    return null
  }

  return response.data.suggestion || null
}

export function useIbanValidator () {
  const currentResourceAuthorization = useAppSelector(getResourceAuthorization)
  const [ibanValidationCheckDone, setIbanValidationCheckDone] = useState(false)
  const [ibanValidated, setIbanValidated] = useState(currentResourceAuthorization?.relationInfo?.ibanValidated ?? false)
  const [ibanSuggestion, setIbanSuggestion] = useState<string | null>(null)
  const [ibanValidationMessage, setIbanValidationMessage] = useState<string | null>(null)
  const validationRequestRef = useRef(0)

  const clearIbanSuggestion = useCallback(() => {
    setIbanSuggestion(null)
    setIbanValidationMessage(null)
  }, [])

  const runIbanValidation = useDebouncedCallback((validationRequest: number, accountNumber: string, accountName: string) => {
    const normalizedAccountNumber = normalizeDutchIban(accountNumber)

    if (!normalizedAccountNumber || !accountName) {
      return
    }

    if (!isValidDutchIban(normalizedAccountNumber) || accountName.length < 3) {
      return
    }

    ibanCheck(accountName, normalizedAccountNumber).then(res => {
      if (validationRequest !== validationRequestRef.current) {
        return
      }

      const suggestion = getSuggestion(res)

      setIbanValidated(!suggestion && Array.isArray(res.data))
      setIbanSuggestion(suggestion)
      setIbanValidationMessage(suggestion ? res.message || null : null)
    }).catch((err: IIbanCheckError) => {
      if (validationRequest !== validationRequestRef.current) {
        return
      }

      const response = err.response?.data
      const suggestion = getSuggestion(response)

      setIbanValidated(false)
      setIbanSuggestion(suggestion)
      setIbanValidationMessage(response?.message || err.message || null)
    }).finally(() => {
      if (validationRequest === validationRequestRef.current) {
        setIbanValidationCheckDone(true)
      }
    })
  }, 1000)

  const validateIbanWithDebounce = useCallback((accountNumber: string, accountName: string) => {
    const validationRequest = validationRequestRef.current + 1

    validationRequestRef.current = validationRequest
    setIbanValidationCheckDone(false)
    setIbanValidated(false)
    clearIbanSuggestion()
    runIbanValidation(validationRequest, accountNumber, accountName)
  }, [clearIbanSuggestion, runIbanValidation])

  return {
    ibanValidationCheckDone,
    ibanValidated,
    ibanSuggestion,
    ibanValidationMessage,
    clearIbanSuggestion,
    validateIbanWithDebounce
  }
}
